import React, { useState, useEffect } from 'react';

import MaterialTable, {
    MTableToolbar,
    MTableHeader,
    MTableBody,
    MTableBodyRow,
} from 'material-table';
import { Grid } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import classNames from 'classnames';
import { LocalStorage } from 'utils/storage';
import { MTableTitle } from './MTableTitle';
import { MTableSelectedActions } from './MTableSeletedActions';
import { CsvBuilder } from 'filefy';
import { uniq } from 'lodash';

import {
    StylesProvider,
    createGenerateClassName,
    makeStyles,
} from '@material-ui/styles';

const additionalColumnData = ({ cellStyle }) => ({
    cellStyle: {
        paddingTop: 5,
        paddingBottom: 5,
        maxHeight: 51,
        height: 51,
        fontSize: '0.8125rem',
        ...cellStyle,
    },
});

const getNestedValue = (object, path) => {
    return path.split('.').reduce((o, i) => o[i], object);
};

const generateClassName = createGenerateClassName({
    productionPrefix: 'mt',
    seed: 'mt',
});

const selStyles = depth =>
    makeStyles(theme => {
        const styles = {};
        for (let i = 0; i < depth; ++i) {
            styles[`row-${i}`] = {
                '& td:nth-child(3) > div': {
                    marginLeft: `-${(depth - i - 1) * 42}px`,
                },
            };
        }
        styles[`header-${depth}`] = {
            '&:nth-child(3) > span': {
                marginLeft: `-${(depth - 1) * 42}px`,
            },
        };
        return styles;
    });

const useStyles = makeStyles(theme => ({
    highlight: {
        backgroundColor: '#e3f2fd',
        color: theme.palette.secondary.main,
        height: 64,
    },
    selectedActions: {
        marginLeft: theme.spacing(2),
    },
    tableHeader: {
        '&:first-child': {
            paddingLeft: 50,
        },
    },
    header: {
        borderTop: `1px solid ${theme.palette.divider}`,
        '& > span > span': {
            width: 24,
            height: 24,
            margin: 0,
        },
    },
    toolbar: {
        '& > div:first-child, & > div:nth-child(2)': {
            flex: 'auto',
        },
    },
}));

const applyColumnData = columns => {
    Object.keys(columns).forEach(column => {
        columns[column] = {
            ...columns[column],
            ...additionalColumnData(columns[column]),
        };
    });

    return columns;
};

const mergeFilters = filters => {
    const newFilters = {};
    filters.forEach(filter => {
        const filtersCopy = Object.assign({}, filter);
        if (!newFilters[filtersCopy.type]) {
            filtersCopy.value = [filtersCopy.value];
            newFilters[filtersCopy.type] = filtersCopy;
        } else {
            newFilters[filtersCopy.type].value.push(filtersCopy.value);
        }
    });

    return Object.values(newFilters);
};

const compareFilter = (filter, value) => {
    const founded = filter.value.findIndex(val => {
        switch (typeof value) {
            case 'number':
                return value === Number(val);
            case 'string':
                return value.toLowerCase().includes(val.toLowerCase());
            case 'boolean':
                return value === val;
            default:
                return true;
        }
    });

    return founded > -1;
};

const filterData = (filters, data, columns) => {
    filters = mergeFilters(filters);
    const returnData = data.filter(item => {
        return filters.every(filter => {
            const column = columns.find(column => column.field === filter.type);
            const value = getNestedValue(
                item,
                (column && column.filteredBy) || filter.type
            );
            if (column.filter) {
                return column.filter(value, filter);
            }

            return compareFilter(filter, value);
        });
    });
    return returnData;
};

const generateCsv = (columns, data) => {
    columns = columns.filter(columnDef => {
        return (
            !columnDef.hidden && columnDef.field && columnDef.export !== false
        );
    });

    data = data.map(rowData =>
        columns.map(columnDef => {
            if (columnDef.exportValue) {
                return columnDef.exportValue(rowData[columnDef.field], rowData);
            }
            return rowData[columnDef.field];
        })
    );

    const builder = new CsvBuilder('export.csv');
    builder
        .setDelimeter(',')
        .setColumns(columns.map(columnDef => columnDef.title))
        .addRows(data)
        .exportFile();
};

const calculateDepth = obj => {
    var depth = 0;
    if (obj.children) {
        obj.children.forEach(d => {
            var tmpDepth = calculateDepth(d);
            if (tmpDepth > depth) {
                depth = tmpDepth;
            }
        });
    }
    return 1 + depth;
};

const getMaxPath = renderData => {
    let maxPath = 0;
    renderData.forEach(item => {
        maxPath = Math.max(maxPath, calculateDepth(item));
    });
    return maxPath;
};

const DEFAULT_ROW_PER_PAGE = 50;
const SETTINGS_ROWS_PER_PAGE = 'MTable::rowsPerPage';

const SettingsStore = {
    setRowsPerPage(number, stateKey = '') {
        LocalStorage.set(SETTINGS_ROWS_PER_PAGE + stateKey, number);
    },
    getRowsPerPage(stateKey = '') {
        const data = LocalStorage.get(
            SETTINGS_ROWS_PER_PAGE + stateKey,
            DEFAULT_ROW_PER_PAGE
        );
        if (typeof data === 'string') {
            const value = parseInt(data, 10);
            if (!Number.isNaN(value)) {
                return value;
            }
        }
        return data;
    },
};

const MTable = ({
    primaryFilters,
    primaryFilterDefault,
    onFiltersChange,
    loading,
    data,
    columns,
    classes = {},
    actions,
    options = {},
    components = {},
    onSelectionChange,
    onSearchSubmit = () => {},
    useCustomSearch,
    searchValue,
    actionMenuItems = [],
    addMenuItems,
    canChange = false,
    detailPanel,
    onRowClick,
    searchPlaceholder = 'Search',
    title = '',
    parentChildData,
    editable = {},
    stateKey,
    onRefresh,
}) => {
    classes = { ...classes, ...useStyles() };
    const [selected, setSelected] = useState([]);
    const [filters, setFilters] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(
        SettingsStore.getRowsPerPage(stateKey)
    );

    useEffect(() => {
        setSelected([]);
    }, [data]);

    const handleSelectionChange = values => {
        let val = uniq(values);
        const selProps = options.selectionProps;
        if (selProps) {
            val = val.filter(row => {
                if (typeof selProps === 'function') {
                    return !selProps(row).disabled;
                }

                return !selProps.disabled;
            });
        }
        setSelected(val);
        if (onSelectionChange) {
            onSelectionChange(val);
        }
    };

    const handleFiltersChange = values => {
        setFilters(values);
        if (onFiltersChange) {
            onFiltersChange(values);
        }
    };

    const handleChangeRowsPerPage = rows => {
        setRowsPerPage(rows);
        SettingsStore.setRowsPerPage(rows, stateKey);
    };

    if (useCustomSearch) {
        options.searchFieldStyle = { display: 'none' };
    }

    const refreshAction = onRefresh
        ? [
              {
                  icon: () => <RefreshIcon fontSize="small" />,
                  tooltip: 'Refresh',
                  isFreeAction: true,
                  onClick: onRefresh,
              },
          ]
        : [];

    let maxPath = 0;
    let rowClasses;

    return (
        <StylesProvider generateClassName={generateClassName}>
            <MaterialTable
                columns={applyColumnData(columns)}
                data={filterData(filters, data, columns)}
                isLoading={loading}
                actions={[...refreshAction, ...(actions || [])]}
                onSelectionChange={handleSelectionChange}
                detailPanel={detailPanel}
                onRowClick={onRowClick}
                parentChildData={parentChildData}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                localization={{
                    toolbar: {
                        searchPlaceholder,
                    },
                    body: {
                        editTooltip: 'Edit',
                    },
                }}
                editable={editable}
                options={{
                    emptyRowsWhenPaging: false,
                    showTextRowsSelected: false,
                    exportButton: true,
                    exportFileName: 'export',
                    pageSize: rowsPerPage,
                    pageSizeOptions: [25, 50, 100, 250, 500, 1000],
                    exportCsv: generateCsv,
                    ...options,
                    selectionProps: rowData => {
                        let selProps = {};
                        if (options.selectionProps) {
                            selProps =
                                typeof options.selectionProps === 'function'
                                    ? options.selectionProps(rowData)
                                    : options.selectionProps;
                        }

                        const { tableData } = rowData;
                        if (options.clearCheckboxMargin) {
                            selProps.style = {
                                marginLeft: (tableData.path.length - 1) * 42,
                                marginRight:
                                    (maxPath - tableData.path.length) * 42,
                            };
                        } else {
                            selProps.style = { marginLeft: 50 };
                        }

                        if (selProps.disabled) {
                            selProps.style.display = 'none';
                        }

                        return {
                            style: {
                                marginLeft: 50,
                            },
                            ...selProps,
                        };
                    },
                }}
                style={{ tableLayout: 'fixed' }}
                title={
                    <MTableTitle
                        primaryFilters={primaryFilters}
                        primaryFilterDefault={primaryFilterDefault}
                        onFiltersChange={handleFiltersChange}
                        filtersValues={filters}
                        useCustomSearch={useCustomSearch}
                        onSearchSubmit={onSearchSubmit}
                        searchValue={searchValue}
                        columns={columns}
                        addMenuItems={addMenuItems}
                        canChange={canChange}
                        data={data}
                        title={title}
                    />
                }
                components={{
                    Toolbar: props => {
                        const { renderData } = props;
                        maxPath = getMaxPath(renderData);
                        rowClasses = selStyles(maxPath);
                        return (
                            <Grid
                                className={classNames({
                                    [classes.highlight]: selected.length > 0,
                                })}
                                container
                                alignItems="center"
                            >
                                {selected.length > 0 && (
                                    <Grid
                                        className={classes.selectedActions}
                                        item
                                        xs="auto"
                                    >
                                        <MTableSelectedActions
                                            selected={selected}
                                            actionMenuItems={actionMenuItems}
                                        />
                                    </Grid>
                                )}
                                {selected.length === 0 && (
                                    <Grid item xs>
                                        <MTableToolbar
                                            {...props}
                                            classes={{ root: classes.toolbar }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        );
                    },
                    Header: props => {
                        let headerClasses = rowClasses();
                        return (
                            <MTableHeader
                                {...props}
                                classes={{
                                    header: classNames({
                                        [classes.tableHeader]:
                                            options.selection,
                                        [classes.header]: true,
                                        [headerClasses[
                                            `header-${maxPath}`
                                        ]]: true,
                                    }),
                                }}
                            />
                        );
                    },
                    Body: props => {
                        return <MTableBody {...props} />;
                    },
                    Row: props => {
                        let classes = rowClasses();
                        return (
                            <MTableBodyRow
                                {...props}
                                classes={{
                                    root: classes[`row-${props.level}`],
                                }}
                            />
                        );
                    },
                    ...components,
                }}
            />
        </StylesProvider>
    );
};

export { MTable };
